import { useToken } from '@chakra-ui/react';
import type { ColumnSizingState } from '@tanstack/react-table';
import { flexRender, type Row } from '@tanstack/react-table';
import type { VirtualItem } from '@tanstack/react-virtual';
import classNames from 'classnames';
import { memo } from 'react';

import { useChartTheme } from '../../utilities';
import styles from './table-chart.module.css';

type OnRenderRow<TData extends object> = (params: {
  rowContent: React.ReactNode;
  rowData: TData;
  rowIndex: number;
  gridRef: HTMLDivElement | null;
}) => React.ReactNode;

type BodyRowProps<TData extends object> = {
  row: Row<TData>;
  rowIndex: number;
  isSelected: boolean;
  isLastRow: boolean;
  height: number;
  translateY: number;
  enableRowSelection: boolean;
  showZebraStripe: boolean;
  zebraStripeColor?: string;
  resolveTextAlign: (
    columnId: string
  ) => 'left' | 'center' | 'right' | undefined;
  onSelectRow?: (data: object) => void;
  onRenderRow?: OnRenderRow<TData>;
  rowClassName?: (rowData: TData, rowIndex: number) => string | undefined;
  autoFitColumns: boolean;
  gridEl: HTMLDivElement | null;
  /** Unused directly; forces a re-render on column resize. */
  columnSizing: ColumnSizingState;
};

const BodyRowInner = <TData extends object = object>({
  row,
  rowIndex,
  isSelected,
  isLastRow,
  height,
  translateY,
  enableRowSelection,
  showZebraStripe,
  zebraStripeColor,
  resolveTextAlign,
  onSelectRow,
  onRenderRow,
  rowClassName,
  autoFitColumns,
  gridEl,
}: BodyRowProps<TData>) => {
  const isZebraRow = showZebraStripe && rowIndex % 2 === 0;
  const rowContent = (
    <tr
      key={row.id}
      className={classNames(
        isZebraRow ? styles.rowEven : styles.rowOdd,
        enableRowSelection && isSelected && styles.rowSelected,
        rowClassName?.(row.original, rowIndex)
      )}
      style={{
        height: `${height}px`,
        transform: `translateY(${translateY}px)`,
        cursor: 'pointer',
        ...(isZebraRow && zebraStripeColor
          ? { '--zebra-stripe-color': zebraStripeColor }
          : {}),
      }}
    >
      {row.getVisibleCells().map((cell, cellIdx) => {
        const align = resolveTextAlign(cell.column.id);
        return (
          <td
            key={cell.id}
            className={classNames(
              styles.cell,
              cellIdx === 0 && styles.firstCol,
              isLastRow && styles.lastRowCell
            )}
            style={{
              ...(autoFitColumns
                ? {}
                : {
                    width: `${cell.column.getSize()}px`,
                    maxWidth: `${cell.column.getSize()}px`,
                  }),
              ...(align ? { textAlign: align } : {}),
            }}
            onClick={() => {
              onSelectRow?.(row.original);
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </tr>
  );

  if (!onRenderRow) return rowContent;

  return (
    <>
      {onRenderRow({
        rowContent,
        rowData: row.original,
        rowIndex,
        gridRef: gridEl,
      })}
    </>
  );
};

// Memoized — a selection toggle re-renders one row, not the whole table.
// Props must stay reference-stable across renders (see columns/resolveTextAlign
// memoization upstream).
const BodyRow = memo(BodyRowInner) as typeof BodyRowInner;

export const useGetBodyRows = <TData extends object = object>({
  items,
  rows,
  enableRowSelection,
  showZebraStripe,
  resolveTextAlign,
  onSelectRow,
  onRenderRow,
  rowClassName,
  autoFitColumns,
  scrollRef,
  columnSizing,
}: {
  items: VirtualItem[];
  rows: Row<TData>[];
  enableRowSelection: boolean;
  showZebraStripe: boolean;
  resolveTextAlign: (
    columnId: string
  ) => 'left' | 'center' | 'right' | undefined;
  onSelectRow?: (data: object) => void;
  onRenderRow?: OnRenderRow<TData>;
  rowClassName?: (rowData: TData, rowIndex: number) => string | undefined;
  autoFitColumns: boolean;
  scrollRef: React.RefObject<HTMLDivElement>;
  columnSizing: ColumnSizingState;
}) => {
  const [lightZebra] = useToken('colors', ['lightBlue.50']);
  const { theme } = useChartTheme();
  const zebraStripeColor = theme === 'dark' ? '#1e2128' : lightZebra;

  return items.map((virtualRow, index) => {
    const row = rows[virtualRow.index];
    if (!row) return null;

    return (
      <BodyRow
        key={row.id}
        row={row}
        rowIndex={virtualRow.index}
        isSelected={enableRowSelection && row.getIsSelected()}
        isLastRow={virtualRow.index === rows.length - 1}
        height={virtualRow.size}
        translateY={virtualRow.start - index * virtualRow.size}
        enableRowSelection={enableRowSelection}
        showZebraStripe={showZebraStripe}
        zebraStripeColor={zebraStripeColor}
        resolveTextAlign={resolveTextAlign}
        onSelectRow={onSelectRow}
        onRenderRow={onRenderRow}
        rowClassName={rowClassName}
        autoFitColumns={autoFitColumns}
        gridEl={scrollRef.current}
        columnSizing={columnSizing}
      />
    );
  });
};
